// src/pages/TelegramApp.tsx
import React, { useEffect, useState } from 'react';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';

export const TelegramApp = () => {
  const {
    user,
    theme,
    isReady,
    webApp,
    isExpanded,
    sendData,
    showAlert,
    showConfirm,
    expand,
    mainButton
  } = useTelegramWebApp();
  
  const [showExpandPrompt, setShowExpandPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isReady && !user) {
      console.error('User data is null. Make sure you are opening through Telegram.');
      setError('User data is not available. Please open this app through a Telegram bot.');
    }
  }, [isReady, user]);

  useEffect(() => {
    if (isReady && webApp) {
      // Устанавливаем заголовок
      webApp.setHeaderColor('#2C3E50');
      
      // Устанавливаем цвет фона
      document.documentElement.style.backgroundColor = theme?.bg_color || '#ffffff';
      
      // Настройка главной кнопки
      mainButton.setText('Continue');
      mainButton.setColor('#2563EB');
      mainButton.setTextColor('#FFFFFF');
      mainButton.show();
      
      // Обработчик клика по главной кнопке
      const handleMainButtonClick = () => {
        sendData(JSON.stringify({ action: 'user_joined' }));
        showAlert('Data sent to Telegram!');
      };
      
      mainButton.onClick(handleMainButtonClick);
      
      // Убираем обработчик при размонтировании
      return () => {
        mainButton.offClick(handleMainButtonClick);
      };
    }
  }, [isReady, theme, sendData, showAlert, mainButton, webApp]);

  // Автоматическое расширение
  useEffect(() => {
    if (isReady && webApp && !isExpanded) {
      try {
        webApp.expand();
      } catch (error) {
        console.error('Failed to auto-expand:', error);
        setShowExpandPrompt(true);
      }
    }
  }, [isReady, webApp, isExpanded]);

  if (!isReady) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        backgroundColor: theme?.bg_color || '#ffffff'
      }}>
        <div>Loading Telegram Mini App...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: theme?.bg_color || '#ffffff',
      color: theme?.text_color || '#000000',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
    }}>
      {error && (
        <div style={{ 
          position: 'fixed', 
          top: 10, 
          right: 10, 
          backgroundColor: '#ff4444', 
          color: 'white', 
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 1000
        }}>
          {error}
        </div>
      )}
      
      <h1 style={{ color: theme?.text_color || '#000000' }}>Welcome to My Telegram Mini App</h1>
      
      {user ? (
        <div style={{ 
          marginBottom: '20px',
          padding: '15px',
          borderRadius: '10px',
          backgroundColor: theme?.secondary_bg_color || '#f0f0f0'
        }}>
          <h2>Hello, {user.first_name} {user.last_name}</h2>
          <p>Telegram ID: {user.id}</p>
          <p>Username: @{user.username}</p>
          <p>Language: {user.language_code}</p>
          <p>Is Premium: {user.is_premium ? 'Yes' : 'No'}</p>
        </div>
      ) : (
        <div style={{ 
          marginBottom: '20px',
          padding: '15px',
          borderRadius: '10px',
          backgroundColor: '#ff4444',
          color: 'white'
        }}>
          <h2>Authentication Error</h2>
          <p>Unable to get user data from Telegram. Make sure you're opening this app through a Telegram bot.</p>
        </div>
      )}
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '15px',
        marginTop: '30px'
      }}>
        <button 
          onClick={() => showAlert('Hello from your Mini App!')}
          style={{ 
            padding: '12px', 
            backgroundColor: theme?.button_color || '#3B82F6',
            color: theme?.button_text_color || '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Show Alert
        </button>
        
        <button 
          onClick={() => {
            showConfirm('Are you sure?', (confirmed) => {
              if (confirmed) {
                sendData(JSON.stringify({ action: 'confirmed' }));
              }
            });
          }}
          style={{ 
            padding: '12px', 
            backgroundColor: theme?.button_color || '#3B82F6',
            color: theme?.button_text_color || '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Confirm Action
        </button>
        
        <button 
          onClick={() => sendData('user_action:share')}
          style={{ 
            padding: '12px', 
            backgroundColor: theme?.button_color || '#3B82F6',
            color: theme?.button_text_color || '#ffffff',
            border: 'none',
            borderRadius: '10px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Send Data to Telegram
        </button>
      </div>
      
      <div style={{ 
        marginTop: '40px', 
        padding: '20px', 
        borderRadius: '10px',
        backgroundColor: theme?.secondary_bg_color || '#f0f0f0'
      }}>
        <h3 style={{ color: theme?.text_color || '#000000' }}>Current Theme</h3>
        <pre style={{ 
          backgroundColor: theme?.bg_color || '#ffffff',
          color: theme?.text_color || '#000000',
          padding: '10px',
          borderRadius: '5px',
          overflow: 'auto'
        }}>
          {JSON.stringify(theme, null, 2)}
        </pre>
      </div>

      {isReady && !isExpanded && showExpandPrompt && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#2c3e50',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '10px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '16px'
        }}>
          <span>Click to expand to full screen</span>
          <button 
            onClick={() => {
              expand();
              setShowExpandPrompt(false);
            }}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              padding: '8px 12px',
              cursor: 'pointer'
            }}
          >
            Expand
          </button>
        </div>
      )}
    </div>
  );
};